"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, Mail } from "lucide-react";
import { useToast } from "@/components/portal/Toast";
import { AuthCard, AuthInput, AuthSubmit } from "@/components/forms/auth-ui";
import { apiForgotPassword, ApiError } from "@/lib/portal/api";
import { readSession } from "@/lib/portal/session";

export function ForgotPasswordForm() {
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const toast = useToast();
  const router = useRouter();

  // Already signed in: password changes happen from the dashboard, not through a reset email.
  useEffect(() => {
    if (readSession()) router.replace("/dashboard");
  }, [router]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const id = identifier.trim();
    if (!id) {
      setError("Enter your email or username.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await apiForgotPassword(id);
      setSentTo(id);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not send the reset link. Please try again.";
      setError(message);
      toast.error("Could not send reset link", message);
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <AuthCard title="Check your email" subtitle={`We sent a password reset link to ${sentTo}.`}>
        <div className="mt-6 flex items-start gap-3 rounded-xl bg-cream p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0e7a53]" />
          <p className="text-sm leading-relaxed text-ink-soft">
            The link expires in 15 minutes. Did not get it? Check the spam folder, or try again with the email you
            registered.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSentTo(null);
            setIdentifier("");
          }}
          className="mt-4 font-medium text-brand-700 hover:underline"
        >
          Use a different email
        </button>
        <p className="mt-6 text-center text-sm text-ink-soft">
          <Link href="/login" className="inline-flex items-center gap-1.5 font-semibold text-brand-700 hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to login
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Forgot password?"
      subtitle="Enter your account email or username and we will send you a password reset link."
    >
      <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
        <AuthInput
          id="fp-email"
          label="Email or username"
          icon={Mail}
          autoComplete="username"
          placeholder="you@example.com"
          value={identifier}
          error={error}
          onChange={(e) => {
            setIdentifier(e.target.value);
            setError(undefined);
          }}
        />
        <AuthSubmit
          busy={busy}
          busyLabel={
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending reset link…
            </>
          }
        >
          <KeyRound className="h-4 w-4" /> Send reset link
        </AuthSubmit>
      </form>

      <p className="mt-6 text-center text-sm text-ink-soft">
        <Link href="/login" className="inline-flex items-center gap-1.5 font-semibold text-brand-700 hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to login
        </Link>
      </p>
    </AuthCard>
  );
}
